import { Dispatch, SetStateAction, useState } from 'react';

import { useQuery } from '@mint-lib/query-client';
import { Chat } from '@mint-sdk/api-ai-assistant';

import type { FileNode, FileSystemNode } from '../../../types/fileSystem.js';
import type { CheckboxItem } from './CheckboxGroup.jsx';
import { DesktopFileSelectionContent } from './DesktopFileSelectionContent.jsx';
import { MobileFileSelectionContent } from './MobileFileSelectionContent.jsx';

type Props = {
  isDisabled: boolean;
  selectedFiles: string[];
  onSelectFiles: Dispatch<SetStateAction<string[]>>;
  isMobile?: boolean;
};

const FileSelection = ({
  isDisabled,
  selectedFiles,
  onSelectFiles,
  isMobile = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading: areFilesLoading } = useQuery({
    ...Chat.listRagDocuments({}),
    enabled: isOpen,
  });
  const availableFiles = data?.filter((f) => f.status === 'success') || [];
  const selectedNames = new Set(selectedFiles);

  const files: Array<FileNode & CheckboxItem> = availableFiles.map((f) => ({
    ...f,
    kind: 'file',
    fileName: f.filename,
    fileType: f.type,
    uploadedDate: f.createdAt,
    id: f.fileId,
    label: f.filename,
    checked: selectedNames.has(f.filename),
  }));

  const handleItemChange = (
    fileName: string,
    checked: boolean,
    kind: FileSystemNode['kind'] = 'file',
  ) => {
    if (kind === 'file') {
      onSelectFiles((prev) => {
        if (checked) {
          return [...prev, fileName];
        } else {
          return prev.filter((file) => file !== fileName);
        }
      });
    }
  };

  const handleSelectAll = () => {
    onSelectFiles(availableFiles.map((f) => f.filename));
  };

  const handleDeselectAll = () => {
    onSelectFiles([]);
  };

  const sharedProps = {
    isDisabled,
    isOpen,
    onOpenChange: setIsOpen,
    files,
    areFilesLoading,
    onItemChange: handleItemChange,
    onSelectAll: handleSelectAll,
    onDeselectAll: handleDeselectAll,
  };

  if (isMobile) {
    return <MobileFileSelectionContent {...sharedProps} />;
  }

  return <DesktopFileSelectionContent {...sharedProps} />;
};

export { FileSelection };
